import React, { useState } from "react";
import { Pencil, Trash, X, MoreVertical, Eye } from "lucide-react";
import {
  Box,
  Modal,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Switch,
  FormControlLabel,
} from "@mui/material";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 0,
};

const CategoryCard = ({
  category,
  onView,
  onEdit,
  onDelete,
  deletingCategoryId,
  isParent,
  isChild,
  parentName,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editImage, setEditImage] = useState(null);
  const [editPreviewUrl, setEditPreviewUrl] = useState(category.image);

  const handleMenuClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditImageSelect = event => {
    const file = event.target.files[0];
    if (file) {
      setEditImage(file);
      setEditPreviewUrl(URL.createObjectURL(file));
    }
  };

  return (
    <div
      className={`bg-white rounded-xl border transition-colors overflow-hidden
      ${
        isParent
          ? "border-[#5652B0] shadow-sm"
          : "border-gray-200 hover:border-[#5652B0]"
      }`}>
      <div className="p-4">
        {/* Category Type Badge */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            {isParent ? (
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-purple-50 text-[#5652B0]">
                Parent Category
              </span>
            ) : (
              <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 flex items-center gap-1">
                <span className="w-1 h-1 rounded-full bg-gray-400" />
                Subcategory of {parentName}
              </span>
            )}
            <span
              className={`px-2.5 py-1 text-xs font-medium rounded-full 
              ${
                category.status === "ACTIVE"
                  ? "bg-green-50 text-green-700"
                  : "bg-red-50 text-red-700"
              }`}>
              {category.status}
            </span>
          </div>
          <IconButton size="small" onClick={handleMenuClick}>
            <MoreVertical size={16} />
          </IconButton>
        </div>

        {/* Category Image */}
        <div className="flex items-center justify-center mb-4">
          <div
            className={`relative w-24 h-24 ${
              isParent ? "ring-2 ring-[#5652B0] ring-offset-2" : ""
            }`}>
            <img
              src={category.image || require("../../../../assets/brand.png")}
              alt={category.name}
              className="w-full h-full rounded-full object-cover"
              onError={e => {
                e.target.onerror = null;
                e.target.src = require("../../../../assets/brand.png");
              }}
            />
          </div>
        </div>

        {/* Category Info */}
        <div className="text-center">
          <h3 className="font-medium text-gray-900">{category.name}</h3>
          <div className="flex items-center justify-center gap-2 mt-1">
            <span className="text-sm text-gray-500">
              {category.products} Products
            </span>
            <span className="text-sm text-gray-400">•</span>
            <span className="text-sm text-gray-500">GST: {category.gst}%</span>
          </div>
          {category.description && (
            <p className="text-sm text-gray-500 mt-2 line-clamp-2">
              {category.description}
            </p>
          )}
        </div>
      </div>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            onView();
            handleMenuClose();
          }}>
          <Eye size={16} className="mr-2" />
          View Details
        </MenuItem>
        <MenuItem
          onClick={() => {
            onEdit(category);
            handleMenuClose();
          }}>
          <Pencil size={16} className="mr-2" />
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            onDelete(category);
            handleMenuClose();
          }}
          className="text-red-600">
          <Trash size={16} className="mr-2" />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Modal */}
      <Modal open={editModal} onClose={() => setEditModal(false)}>
        <Box sx={modalStyle}>
          <div className="border-b border-gray-200">
            <div className="px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-medium text-gray-900">
                Edit Category
              </h2>
              <IconButton size="small" onClick={() => setEditModal(false)}>
                <X size={18} />
              </IconButton>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-2 gap-6">
              <TextField
                label="Category Name"
                variant="outlined"
                size="small"
                fullWidth
                defaultValue={category.name}
              />

              <TextField
                label="Parent Category"
                variant="outlined"
                size="small"
                select
                fullWidth
                defaultValue={category.parent || ""}>
                <MenuItem value="">None (Top Level)</MenuItem>
                {/* Parent categories will be passed as prop */}
              </TextField>

              <TextField
                label="GST Rate (%)"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                required
                defaultValue={category.gst}
              />

              <TextField
                label="Display Order"
                variant="outlined"
                size="small"
                type="number"
                fullWidth
                defaultValue={category.display_order || 0}
              />

              <TextField
                label="Description"
                variant="outlined"
                size="small"
                multiline
                rows={3}
                fullWidth
                className="col-span-2"
                defaultValue={category.description}
              />

              <div className="col-span-2">
                <p className="text-sm font-medium text-gray-700 mb-3">
                  Category Image
                </p>
                <div className="flex items-start gap-6">
                  {/* Image Preview */}
                  <div className="w-32 h-32 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50">
                    {editPreviewUrl ? (
                      <img
                        src={editPreviewUrl}
                        alt="Category preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center p-4">
                        <img
                          src={require("../../../../assets/upload.png")}
                          alt="upload"
                          className="w-12 h-12 mx-auto mb-2 opacity-50"
                        />
                        <p className="text-xs text-gray-500">
                          No image selected
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Upload Controls */}
                  <div className="flex-1">
                    <div className="flex flex-col gap-3">
                      <div
                        className={`border-2 border-dashed rounded-lg transition-colors ${
                          editPreviewUrl
                            ? "border-green-200 bg-green-50"
                            : "border-gray-200 hover:border-[#5652B0]"
                        }`}>
                        <label className="flex flex-col items-center justify-center py-4 px-6 cursor-pointer">
                          <div className="flex flex-col items-center">
                            {editPreviewUrl ? (
                              <>
                                <div className="p-2 rounded-full bg-green-100 mb-2">
                                  <img
                                    src={require("../../../../assets/upload.png")}
                                    alt="upload"
                                    className="w-5 h-5"
                                  />
                                </div>
                                <p className="text-sm font-medium text-green-600">
                                  Image selected
                                </p>
                                <p className="text-xs text-green-500 mt-1">
                                  Click to change image
                                </p>
                              </>
                            ) : (
                              <>
                                <div className="p-2 rounded-full bg-gray-100 mb-2">
                                  <img
                                    src={require("../../../../assets/upload.png")}
                                    alt="upload"
                                    className="w-5 h-5"
                                  />
                                </div>
                                <p className="text-sm font-medium text-gray-700">
                                  Drop your image here, or{" "}
                                  <span className="text-[#5652B0]">browse</span>
                                </p>
                                <p className="text-xs text-gray-500 mt-1">
                                  PNG, JPG up to 5MB
                                </p>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            onChange={handleEditImageSelect}
                          />
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-2">
                <FormControlLabel
                  control={
                    <Switch
                      checked={category.status === "ACTIVE"}
                      onChange={() => {}}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                          color: "#5652B0",
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#5652B0",
                          },
                      }}
                    />
                  }
                  label="Active Status"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setEditModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium hover:bg-[#4A47A3]">
                Save Changes
              </button>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal open={deleteModal} onClose={() => setDeleteModal(false)}>
        <Box sx={modalStyle}>
          <div className="p-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4">
              <img
                src={require("../../../../assets/deletelogo.png")}
                alt="Delete"
                className="w-full h-full"
              />
            </div>
            <h2 className="text-lg font-medium text-gray-900 mb-2">
              Delete Category
            </h2>
            <p className="text-gray-500 mb-6">
              Are you sure you want to delete this category? This action cannot
              be undone.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setDeleteModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700">
                Delete Category
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
};

export default CategoryCard;
